export const HOSTED_SUPABASE_HOST = "pourxjxglnwybwiayskk.supabase.co";
export const BOSTON_SUPABASE_HOST = "sb-osirisbrainerp.osirisbrain.com";

export const ALLOWED_SUPABASE_HOSTS = [
  HOSTED_SUPABASE_HOST,
  BOSTON_SUPABASE_HOST,
] as const;

export type AllowedSupabaseHost = (typeof ALLOWED_SUPABASE_HOSTS)[number];

type SupabaseRuntimeInput = {
  url?: string;
  anonKey?: string;
  expectedHost?: string;
};

type SupabaseRuntimeConfig = {
  url: string;
  anonKey: string;
  hostname: AllowedSupabaseHost;
};

const allowedHosts = new Set<string>(ALLOWED_SUPABASE_HOSTS);

function decodedJwtRole(key: string): unknown {
  const segments = key.split(".");
  if (segments.length !== 3) return undefined;

  try {
    const base64 = segments[1]
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(segments[1].length / 4) * 4, "=");
    const payload = JSON.parse(globalThis.atob(base64)) as {
      role?: unknown;
    };
    return payload.role;
  } catch {
    return undefined;
  }
}

export function resolveSupabaseRuntimeConfig(
  input: SupabaseRuntimeInput,
): SupabaseRuntimeConfig {
  const rawUrl = input.url?.trim();
  if (!rawUrl) {
    throw new Error("VITE_SUPABASE_URL is required");
  }

  const anonKey = input.anonKey?.trim();
  if (!anonKey) {
    throw new Error("VITE_SUPABASE_ANON_KEY is required");
  }

  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    throw new Error("VITE_SUPABASE_URL must be a valid URL");
  }

  if (url.protocol !== "https:") {
    throw new Error("VITE_SUPABASE_URL must use HTTPS");
  }
  if (url.username || url.password) {
    throw new Error("VITE_SUPABASE_URL must not contain credentials");
  }
  if (url.port) {
    throw new Error("VITE_SUPABASE_URL must not override the HTTPS port");
  }
  if (url.pathname !== "/") {
    throw new Error("VITE_SUPABASE_URL must use the API root");
  }
  if (url.search) {
    throw new Error("VITE_SUPABASE_URL must not contain a query");
  }
  if (url.hash) {
    throw new Error("VITE_SUPABASE_URL must not contain a fragment");
  }

  const hostname = url.hostname.toLowerCase();
  if (!allowedHosts.has(hostname)) {
    throw new Error("VITE_SUPABASE_URL host is not in the project allow-list");
  }

  const expectedHost = input.expectedHost?.trim().toLowerCase();
  if (expectedHost && !allowedHosts.has(expectedHost)) {
    throw new Error("VITE_SUPABASE_EXPECTED_HOST is not in the project allow-list");
  }
  if (expectedHost && hostname !== expectedHost) {
    throw new Error("VITE_SUPABASE_URL does not match VITE_SUPABASE_EXPECTED_HOST");
  }

  if (anonKey.startsWith("sb_secret_") || decodedJwtRole(anonKey) === "service_role") {
    throw new Error("VITE_SUPABASE_ANON_KEY must not contain a service_role key");
  }

  return {
    url: url.origin,
    anonKey,
    hostname: hostname as AllowedSupabaseHost,
  };
}
